<template>
  <div class="student-email-report">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">{{ $t("Book List Summary Report") }}</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ studentCount }} {{ $t("Students") }}
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>{{ $t("Print Report") }}</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>{{ $t("Close") }}</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem" />
      <p class="mt-3 font-weight-bold">
        {{ $t("Loading book list summary...") }}
      </p>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" class="mt-3" @click="loadReportData">{{
        $t("Retry")
      }}</b-button>
    </div>

    <div v-else class="report-page">
      <div class="report-header-modern mb-4">
        <div class="d-flex align-items-center">
          <div v-if="schoolLogo" class="logo-wrapper mr-3">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
          </div>
          <div class="header-text-block">
            <h2 class="school-name-full mb-0">
              {{ campusName
              }}<span v-if="campusBranch" class="text-muted">
                - {{ campusBranch }}</span
              >
            </h2>
            <div class="report-info-line mt-1">
              <span class="report-name-text">{{
                $t("Book List Summary Report")
              }}</span>
              <span class="date-range-text">
                <strong>{{ $t("Generated on") }} {{ currentDateTime }}</strong>
              </span>
            </div>
            <div v-if="filterSummary" class="text-muted mt-50 small">
              {{ filterSummary }}
            </div>
          </div>
        </div>
      </div>

      <div class="summary-section summary-section--status mb-2">
        <div class="summary-card">
          <span class="label">{{ $t("Students") }}</span>
          <span class="value text-primary">{{ studentCount }}</span>
        </div>
        <div class="summary-card">
          <span class="label">{{ $t("Pending") }}</span>
          <span class="value text-secondary">{{ statusSummary.pending }}</span>
        </div>
        <div class="summary-card">
          <span class="label">{{ $t("Partial") }}</span>
          <span class="value text-warning">{{ statusSummary.partial }}</span>
        </div>
        <div class="summary-card">
          <span class="label">{{ $t("Closed") }}</span>
          <span class="value text-success">{{ statusSummary.closed }}</span>
        </div>
      </div>

      <div class="summary-section mb-3">
        <div class="summary-card">
          <span class="label">{{ $t("Total Required") }}</span>
          <span class="value">{{ totals.required }}</span>
        </div>
        <div class="summary-card">
          <span class="label">{{ $t("Total Received") }}</span>
          <span class="value text-success">{{ totals.received }}</span>
        </div>
        <div class="summary-card">
          <span class="label">{{ $t("Parent Confirmed") }}</span>
          <span class="value text-info">{{ totals.parentConfirmed }}</span>
        </div>
        <div class="summary-card">
          <span class="label">{{ $t("Remaining") }}</span>
          <span class="value text-warning">{{ totals.remaining }}</span>
        </div>
        <div class="summary-card">
          <span class="label">{{ $t("Pending Parent Confirmation") }}</span>
          <span class="value text-primary">{{ totals.parentPending }}</span>
        </div>
      </div>

      <div class="report-table-block mb-3">
        <h6 class="report-table-title">{{ $t("Student Summary") }}</h6>
        <div class="table-container">
          <table class="ledger-style-table">
            <thead>
              <tr>
                <th class="text-center" style="width: 5%">#</th>
                <th class="text-center" style="width: 10%">GR. NO</th>
                <th class="text-left" style="width: 22%">STUDENT</th>
                <th class="text-center" style="width: 10%">STATUS</th>
                <th class="text-center" style="width: 11%">TOTAL REQ</th>
                <th class="text-center" style="width: 11%">TOTAL RCV</th>
                <th class="text-center" style="width: 11%">TOTAL REM</th>
                <th class="text-center" style="width: 20%">
                  PENDING PARENT CONFIRM
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(student, index) in reportStudents"
                :key="'student-' + student.id"
              >
                <td class="text-center font-weight-bold">{{ index + 1 }}</td>
                <td class="text-center gr-cell">{{ student.grNo || "—" }}</td>
                <td class="text-left font-weight-bold">
                  {{ (student.studentName || "—").toUpperCase() }}
                </td>
                <td class="text-center">
                  <span
                    :class="['status-pill', 'status-pill--' + student.status]"
                  >
                    {{ formatStatus(student.status) }}
                  </span>
                </td>
                <td class="text-center font-weight-bold">
                  {{ student.totalRequired }}
                </td>
                <td class="text-center text-success font-weight-bold">
                  {{ student.totalReceived }}
                </td>
                <td class="text-center text-warning font-weight-bold">
                  {{ student.totalRemaining }}
                </td>
                <td class="text-center text-primary font-weight-bold">
                  {{ student.totalParentPending }}
                </td>
              </tr>
              <tr v-if="!reportStudents.length">
                <td colspan="8" class="text-center py-4 no-data">
                  <feather-icon
                    icon="InboxIcon"
                    size="28"
                    class="text-muted mb-1"
                  />
                  <p class="mb-0">
                    {{ $t("No records found for the selected criteria") }}
                  </p>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="reportStudents.length" class="report-table-block">
        <h6 class="report-table-title">{{ $t("Item Details") }}</h6>
        <div class="table-container">
          <table class="ledger-style-table">
            <thead>
              <tr>
                <th class="text-center" style="width: 4%">#</th>
                <th class="text-center" style="width: 9%">GR. NO</th>
                <th class="text-left" style="width: 16%">STUDENT</th>
                <th class="text-left" style="width: 16%">CATEGORY</th>
                <th class="text-center" style="width: 8%">REQ</th>
                <th class="text-center" style="width: 8%">RCV</th>
                <th class="text-center" style="width: 9%">P.CONF</th>
                <th class="text-center" style="width: 8%">REM</th>
                <th class="text-center" style="width: 11%">PARENT PEND</th>
                <th class="text-center" style="width: 11%">STATUS</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in flatItemRows" :key="'item-' + row.key">
                <td class="text-center font-weight-bold">{{ index + 1 }}</td>
                <td class="text-center gr-cell">{{ row.grNo || "—" }}</td>
                <td class="text-left">
                  {{ (row.studentName || "—").toUpperCase() }}
                </td>
                <td class="text-left font-weight-bold">
                  {{ row.categoryName || "—" }}
                </td>
                <td class="text-center">{{ row.requiredQty }}</td>
                <td class="text-center">{{ row.receivedQty }}</td>
                <td class="text-center">{{ row.parentConfirmedQty }}</td>
                <td class="text-center">{{ row.remainingQty }}</td>
                <td class="text-center">{{ row.parentPendingQty }}</td>
                <td class="text-center">
                  <span :class="['status-pill', 'status-pill--' + row.status]">
                    {{ formatStatus(row.status) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";

export default {
  name: "BookListSummaryPrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      reportStudents: [],
      reportSummary: null,
      campusName: "MY SKOOL",
      campusBranch: "",
      schoolLogo: null,
      db: "",
      cID: "",
      classID: "",
      sectionID: "",
      status: "",
      sID: "",
    };
  },
  computed: {
    currentDateTime() {
      const now = new Date();
      return now.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    studentCount() {
      if (this.reportSummary && this.reportSummary.totalStudents != null) {
        return Number(this.reportSummary.totalStudents) || 0;
      }
      return this.reportStudents.length;
    },
    statusSummary() {
      return {
        pending: Number(this.reportSummary && this.reportSummary.pending) || 0,
        partial: Number(this.reportSummary && this.reportSummary.partial) || 0,
        closed: Number(this.reportSummary && this.reportSummary.closed) || 0,
      };
    },
    totals() {
      let required = 0;
      let received = 0;
      let parentConfirmed = 0;
      let remaining = 0;
      let parentPending = 0;

      this.reportStudents.forEach((student) => {
        required += Number(student.totalRequired) || 0;
        received += Number(student.totalReceived) || 0;
        remaining += Number(student.totalRemaining) || 0;
        parentPending += Number(student.totalParentPending) || 0;
        (student.items || []).forEach((item) => {
          parentConfirmed += Number(item.parentConfirmedQty) || 0;
        });
      });

      return { required, received, parentConfirmed, remaining, parentPending };
    },
    flatItemRows() {
      const rows = [];
      this.reportStudents.forEach((student) => {
        (student.items || []).forEach((item) => {
          rows.push({
            key: `${student.id}-${item.id}`,
            grNo: student.grNo,
            studentName: student.studentName,
            categoryName: item.categoryName,
            requiredQty: item.requiredQty,
            receivedQty: item.receivedQty,
            parentConfirmedQty: item.parentConfirmedQty,
            remainingQty: item.remainingQty,
            parentPendingQty: item.parentPendingQty,
            status: item.status,
          });
        });
      });
      return rows;
    },
    filterSummary() {
      const parts = [];
      if (this.classID) parts.push(`${this.$t("Class")}: ${this.classID}`);
      if (this.sectionID)
        parts.push(`${this.$t("Section")}: ${this.sectionID}`);
      if (this.status) parts.push(`${this.$t("Status")}: ${this.status}`);
      if (this.sID) parts.push(`${this.$t("Student")}: ${this.sID}`);
      return parts.join(" · ");
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    initReport() {
      const query = this.$route.query;
      this.db = query.db || this.$store.state.userData.db;
      this.cID = query.cID || this.$store.state.userData.cId || 0;
      this.classID = query.classID || "";
      this.sectionID = query.sectionID || "";
      this.status = query.status || "";
      this.sID = query.sID || "";

      if (!this.db) {
        this.error = this.$t(
          "Database identifier is missing. Please close and try again."
        );
        return;
      }

      this.loadCampusInfo();
      this.loadReportData();
    },
    async loadCampusInfo() {
      try {
        const url = "https://apiv26.myskool.app/api/campuses";
        const response = await axios.get(url, {
          params: { db: this.db, cId: this.cID },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (
          response.data?.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find((c) => c.id == this.cID);
          if (match) {
            this.campusName = match.name;
            this.campusBranch = this.cID == 0 ? "All Campuses" : match.branch;
            this.schoolLogo = match.logo;
          }
        }
      } catch (e) {
        const branches = this.$store.state.branches || [];
        const currentBranch =
          branches.find((b) => b.id == this.cID) ||
          this.$store.state.currentBranch;
        if (currentBranch) {
          this.campusName =
            currentBranch.organizationName ||
            currentBranch.branch ||
            "MY SKOOL";
          this.campusBranch = currentBranch.branch || "";
        }
      }
    },
    mapItem(item) {
      const requiredQty = Number(
        item.requiredQty != null
          ? item.requiredQty
          : item.qty != null
          ? item.qty
          : 0
      );
      const receivedQty = Number(
        item.receivedQty != null ? item.receivedQty : 0
      );
      const parentConfirmedQty = Number(
        item.parentConfirmedQty != null ? item.parentConfirmedQty : 0
      );
      return {
        id: item.id != null ? item.id : item.recordItemID,
        categoryName: item.categoryName || item.name || item.category || "—",
        requiredQty,
        receivedQty,
        parentConfirmedQty,
        remainingQty:
          item.remainingQty != null
            ? Number(item.remainingQty)
            : Math.max(requiredQty - receivedQty, 0),
        parentPendingQty:
          item.parentPendingQty != null
            ? Number(item.parentPendingQty)
            : Math.max(receivedQty - parentConfirmedQty, 0),
        status: (item.status || "pending").toLowerCase(),
      };
    },
    mapStudent(row) {
      const items = row.items || row.bookListItems || [];
      return {
        id: row.recordID != null ? row.recordID : row.id,
        sID: row.sID,
        grNo: row.grNo || row.gr || "—",
        studentName: row.studentName || row.name || "—",
        className: row.className || row.meta_class || "—",
        sectionName: row.sectionName || row.meta_section || "—",
        status: (row.status || "pending").toLowerCase(),
        totalRequired: Number(
          row.totalRequired != null ? row.totalRequired : 0
        ),
        totalReceived: Number(
          row.totalReceived != null ? row.totalReceived : 0
        ),
        totalRemaining: Number(
          row.totalRemaining != null ? row.totalRemaining : 0
        ),
        totalParentPending: Number(
          row.totalParentPending != null ? row.totalParentPending : 0
        ),
        items: (Array.isArray(items) ? items : []).map((item) =>
          this.mapItem(item)
        ),
      };
    },
    normalizeReportData(responseData) {
      if (!responseData) {
        return { summary: null, rows: [] };
      }

      let summary = null;
      let rows = [];

      if (responseData.summary && Array.isArray(responseData.rows)) {
        summary = responseData.summary;
        rows = responseData.rows;
      } else if (Array.isArray(responseData.rows)) {
        rows = responseData.rows;
        summary = responseData.summary || null;
      } else if (Array.isArray(responseData)) {
        rows = responseData;
      } else if (Array.isArray(responseData.data)) {
        rows = responseData.data;
      } else if (Array.isArray(responseData.students)) {
        rows = responseData.students;
      }

      return {
        summary,
        rows: rows.map((row) => this.mapStudent(row)),
      };
    },
    formatStatus(status) {
      if (!status) return "—";
      return status.charAt(0).toUpperCase() + status.slice(1);
    },
    async loadReportData() {
      this.loading = true;
      this.error = null;

      try {
        const domain = (
          this.$store.state.domain || "https://apiv26.myskool.app/api/"
        ).replace(/\/?$/, "/");
        let url =
          domain +
          "BookListRecord/ReportSummary?db=" +
          encodeURIComponent(this.db) +
          "&campusID=" +
          this.cID;
        if (this.classID) url += "&classID=" + this.classID;
        if (this.sectionID) url += "&sectionID=" + this.sectionID;
        if (this.status) url += "&status=" + this.status;
        if (this.sID) url += "&sID=" + this.sID;

        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        const payload =
          response.data && response.data.data
            ? response.data.data
            : response.data;
        const result = this.normalizeReportData(payload);
        this.reportSummary = result.summary;
        this.reportStudents = result.rows;
      } catch (err) {
        console.error("Error fetching book list summary:", err);
        this.error = this.$t(
          "Failed to retrieve report data. Please ensure the API endpoint is accessible."
        );
      } finally {
        this.loading = false;
      }
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.student-email-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: "Outfit", "Inter", sans-serif;
  color: #2d3436;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
    letter-spacing: -0.5px;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  &:active {
    transform: scale(0.96);
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;

  &:hover {
    background: linear-gradient(135deg, #5b4cc4 0%, #8c82eb 100%);
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.35);
    transform: translateY(-1px);
  }
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;

  &:hover {
    background: #fdfdfd;
    color: #e74c3c;
    border-color: #fab1a0;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }
}

.report-page {
  background: #ffffff;
  padding: 3rem 2.5rem;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  width: 98%;
  max-width: 1200px;
  margin: 30px auto;
  box-sizing: border-box;
}

.report-header-modern {
  border-bottom: 2px solid #333;
  padding-bottom: 20px;

  .school-logo-img {
    height: 130px;
    width: auto;
    object-fit: contain;
  }

  .school-name-full {
    font-size: 2rem;
    font-weight: 800;
    color: #000;
    text-transform: uppercase;
    letter-spacing: -0.5px;
  }

  .report-info-line {
    font-size: 1.1rem;
    color: #444;
    display: flex;
    align-items: center;
    flex-wrap: wrap;

    .report-name-text {
      font-weight: 700;
      border-right: 2px solid #ddd;
      padding-right: 15px;
      margin-right: 15px;
    }
  }
}

.summary-section {
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  gap: 1.5rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #eee;

  &--status {
    background: linear-gradient(180deg, #faf9ff 0%, #f8f9fa 100%);
    border-color: #e0d8ff;
  }

  .summary-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 90px;

    .label {
      font-size: 0.8rem;
      font-weight: 700;
      color: #636e72;
      text-transform: uppercase;
      letter-spacing: 1px;
      margin-bottom: 4px;
      text-align: center;
    }

    .value {
      font-size: 1.4rem;
      font-weight: 800;
    }
  }
}

.report-table-block {
  margin-top: 0.5rem;
}

.report-table-title {
  font-size: 0.95rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #2c3e50;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e9ecef;
}

.status-pill {
  display: inline-block;
  padding: 0.2rem 0.65rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: capitalize;

  &--pending {
    background: #eef1f5;
    color: #636e72;
  }

  &--partial {
    background: #fff3cd;
    color: #856404;
  }

  &--closed {
    background: #d4edda;
    color: #155724;
  }
}

.ledger-style-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  border: 1px solid #dee2e6;
  background: white;

  th,
  td {
    border: 1px solid #dee2e6;
    padding: 12px 15px;
    box-sizing: border-box;
    font-size: 0.95rem;
  }

  thead {
    background: #2c3e50 !important;

    th {
      color: white !important;
      font-weight: 800;
      text-transform: uppercase;
      font-size: 0.9rem;
      letter-spacing: 0.5px;
    }
  }

  tbody tr:nth-child(even) {
    background-color: #f8f9fa;
  }

  .gr-cell {
    font-weight: 700;
    color: #6c5ce7;
  }
}

@page {
  size: A4 portrait;
  margin: 0;
}

@media print {
  .no-print {
    display: none !important;
  }

  body {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
    color: #000 !important;
  }

  .student-email-report {
    background: white !important;
    padding: 0 !important;
  }

  .report-page {
    box-shadow: none !important;
    border: none !important;
    padding: 1.2cm 0.8cm !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
  }

  .ledger-style-table {
    font-size: 9pt !important;
    width: 100% !important;

    th,
    td {
      padding: 6px 10px !important;
      border: 1px solid #000 !important;
      color: #000 !important;
    }

    thead th {
      background: #f1f2f6 !important;
      color: #000 !important;
      font-weight: 800 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }

  .report-header-modern {
    margin-bottom: 25px !important;
    padding-bottom: 15px !important;
    border-bottom: 4px solid #000 !important;

    .school-logo-img {
      height: 140px !important;
      max-width: 350px !important;
      object-fit: contain !important;
    }

    .school-name-full {
      font-size: 1.8rem !important;
    }
  }

  .summary-section {
    margin: 5px 0 !important;
    padding: 8px !important;
    border: 1px solid #000 !important;
    display: flex !important;
    flex-direction: row !important;
    justify-content: space-around !important;

    .summary-card {
      .label {
        font-size: 0.7rem !important;
      }

      .value {
        font-size: 1rem !important;
      }
    }
  }

  .report-table-title {
    font-size: 0.85rem !important;
    color: #000 !important;
    border-bottom-color: #000 !important;
  }

  .status-pill {
    border: 1px solid #000 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
</style>
